"use client";

import { SbBlokData } from "@storyblok/react";
import { StoryblokLink } from "@wojo/storyblok";
import { createContext, useContext, useRef } from "react";
import {
    BusinessCode,
    CurrencyCode,
    PaymentScheduleFrequency,
    TourContentFragmentFragment,
    useAlternateTourYearContentQuery,
} from "../../../../generated/graphql";
import { useAlternateYearContext } from "../alternate-years-context";

export enum TripBuilderDepartureDetailType {
    ClassLevel = "classLevel",
    Extension = "extension",
}

export enum TripBuilderFeaturedFilter {
    PriceWithFlight = "priceWithFlights",
    AvailableOnline = "availableOnline",
    DealsOnly = "dealsOnly",
    ExtensionsOnly = "extensionsOnly",
}

type TourPageConfig = {
    flightsDescription: string;
    flightsInclusions: string;
    flightsInclusionsHeading: string;
    accommodationsDescription: string;
    disableSocialShare: boolean;
    isTripBuilderStickyButtonVisible: boolean;
    tourPageSectionOrder: {
        section: string;
    }[];
    heroSummaryOrder: {
        section: string;
    }[];
    tripBuilderDepartureDetailType: TripBuilderDepartureDetailType;
    tripBuilderFeaturedFilter?: TripBuilderFeaturedFilter | "" | null;
    departuresFilters?: string[];
    tripBuilderMarketingBanner?: string | null;
    displayTripBuilderContactForm?: boolean;
    displayLowestLeadInPrice: boolean;
    displayLowestPayment: boolean;
    tourPageMarketing?: {
        content: {
            primaryMessage: SbBlokData[];
            secondaryMessage: SbBlokData[];
            tertiaryMessage: SbBlokData[];
        };
    };
    cancellationMessaging?: string;
    cancellationMessageModalContent?: string;
    displayTripBuilderYearToggle?: boolean;
    defaultUSCustomary?: boolean;
};

export type TourPageAlternateYear = {
    yearLabel: string;
    tourId: string;
};

export type TourPageSpecialDeparture = {
    text: string;
    link: StoryblokLink;
};

export type WorkHours = {
    startTime?: string;
    endTime?: string;
    isClosed?: boolean;
};

type TourPageContactConfig = {
    phoneNumber?: string;
    workHoursMonday?: WorkHours[];
    workHoursTuesday?: WorkHours[];
    workHoursWednesday?: WorkHours[];
    workHoursThursday?: WorkHours[];
    workHoursFriday?: WorkHours[];
    workHoursSaturday?: WorkHours[];
    workHoursSunday?: WorkHours[];
    phoneNumberFormatted?: string;
};

type TourPageContextValue = React.PropsWithChildren<{
    pageConfig: TourPageConfig;
    tourContent: TourContentFragmentFragment;
    contactConfig?: TourPageContactConfig;
    colorPalette: string;
    specialDepartures?: TourPageSpecialDeparture[];
    pdfUrl?: string | null;
    primaryMessageTitle?: string;
    primaryMessageBody?: string;
    primaryMessageStatus?: string;
    ancestors?: { href: string; name: string }[] | null;
    reviewAggregate?: {
        avgRating: number;
        nbReviews: number;
    };
    alternateYears?: TourPageAlternateYear[];
    tourCodes?: string[];
    tourNameOverride?: string | null;
    navRef?: React.MutableRefObject<HTMLElement | null> | null;
    callToBookDpd?: string;
    defaultPaymentFrequency?: PaymentScheduleFrequency | null;
    tripBuilderPriceHelpText?: string | null;
    storyblokPageUid: string;
    relatedToursHeading?: string;
    relatedToursAlgoliaContext?: string;
}>;

const TourPageContext = createContext<TourPageContextValue>({
    pageConfig: {} as TourPageConfig,
    tourContent: {} as TourContentFragmentFragment,
    colorPalette: "",
    navRef: null,
    storyblokPageUid: "",
});

export const TourPageContextProvider: React.FC<TourPageContextValue> = ({
    children,
    ...value
}) => {
    const navRef = useRef<HTMLElement | null>(null);
    return (
        <TourPageContext.Provider value={{ ...value, navRef }}>
            {children}
        </TourPageContext.Provider>
    );
};

export const useTourPageContext = () => {
    const contextValue = useContext(TourPageContext);
    const alternateYearContext = useAlternateYearContext();

    const alternateYearData = useAlternateTourYearContentQuery({
        variables: {
            tourContentId: alternateYearContext?.tourUUID ?? "",
            language: process.env.NEXT_PUBLIC_STORYBLOK_LANGUAGE ?? "",
            businessCode: process.env.NEXT_PUBLIC_BUSINESS_CODE as BusinessCode,
            currencyCode: process.env.NEXT_PUBLIC_CURRENCY_CODE as CurrencyCode,
        },
        skip: !alternateYearContext?.tourUUID,
    });

    const tourContent = {
        ...(alternateYearData.data?.tourContent ?? contextValue.tourContent),
        name: contextValue.tourNameOverride || contextValue.tourContent.name,
    };

    return {
        ...contextValue,
        tourContent,
    };
};

export const useTourCodes = () => {
    const { tourCodes, tourContent } = useTourPageContext();
    if (tourCodes?.length) {
        return tourCodes;
    }
    return tourContent.tourCode ? [tourContent.tourCode] : null;
};
